import {
  VoiceAccountMetadata,
  VoiceAccountModel,
  VoiceAgentVoice,
} from "@jugl-web/rest-api";
import { VoiceGeneralFormValues, VoiceTransferFormValues } from "../types";

const DEFAULT_VOICE: VoiceAgentVoice = "Zoe";

export const MAX_CALL_DURATION_MIN = 15;
export const DEFAULT_MAX_CALL_DURATION_MIN = MAX_CALL_DURATION_MIN;
export const DEFAULT_MAX_CONCURRENT_CALLS = 5;
export const MAX_DAILY_CALLS_LIMIT = 100;
export const MAX_CONCURRENT_CALLS = 10;
const SECONDS_PER_MINUTE = 60;
export const MIN_CALLS_LIMIT = 1;
export const DAILY_CALL_LIMIT_OPTIONS = [1, 5, 10, 20, 50, 100] as const;

export const callDurationSecondsToMinutes = (seconds: number): number =>
  Math.round(seconds / SECONDS_PER_MINUTE);

export const callDurationMinutesToSeconds = (minutes: number): number =>
  minutes * SECONDS_PER_MINUTE;

export const getVoiceGeneralFormValues = (
  account?: VoiceAccountModel | null
): VoiceGeneralFormValues => {
  const metadata = account?.metadata;
  const maxDurationSeconds = metadata?.call_limiter?.max_duration;
  const dayLimit = metadata?.call_limiter?.day_limit;
  const activeCalls = metadata?.call_limiter?.active_calls;

  return {
    use_base_instructions: metadata?.use_base_instructions ?? true,
    instructions: metadata?.instructions ?? "",
    welcome_message: metadata?.greeting ?? "",
    voice: metadata?.voice ?? DEFAULT_VOICE,
    daily_calls_limit:
      dayLimit == null
        ? null
        : Math.min(MAX_DAILY_CALLS_LIMIT, Math.max(MIN_CALLS_LIMIT, dayLimit)),
    max_call_duration_min:
      maxDurationSeconds == null
        ? DEFAULT_MAX_CALL_DURATION_MIN
        : Math.min(
            DEFAULT_MAX_CALL_DURATION_MIN,
            Math.max(
              MIN_CALLS_LIMIT,
              callDurationSecondsToMinutes(maxDurationSeconds)
            )
          ),
    max_concurrent_calls:
      activeCalls == null
        ? DEFAULT_MAX_CONCURRENT_CALLS
        : Math.min(
            MAX_CONCURRENT_CALLS,
            Math.max(MIN_CALLS_LIMIT, activeCalls)
          ),
  };
};

export const getVoiceTransferFormValues = (
  account?: VoiceAccountModel | null
): VoiceTransferFormValues => ({
  transfer_enabled: !!account?.metadata?.handover_phone,
  transfer_number: account?.metadata?.handover_phone ?? null,
  when_to_handover: account?.metadata?.when_to_handover ?? "",
});

export const buildVoiceMetadata = (
  account: VoiceAccountModel | null | undefined,
  patch: Partial<VoiceAccountMetadata>
): VoiceAccountMetadata => {
  const existingLimiter = account?.metadata?.call_limiter;

  return {
    greeting: account?.metadata?.greeting ?? null,
    instructions: account?.metadata?.instructions ?? null,
    use_base_instructions: account?.metadata?.use_base_instructions ?? true,
    handover_phone: account?.metadata?.handover_phone ?? null,
    when_to_handover: account?.metadata?.when_to_handover ?? null,
    voice: account?.metadata?.voice ?? DEFAULT_VOICE,
    timezone: account?.metadata?.timezone ?? null,
    ...patch,
    call_limiter: {
      day_limit: existingLimiter?.day_limit ?? null,
      max_duration:
        existingLimiter?.max_duration ??
        callDurationMinutesToSeconds(DEFAULT_MAX_CALL_DURATION_MIN),
      active_calls:
        existingLimiter?.active_calls ?? DEFAULT_MAX_CONCURRENT_CALLS,
      ...patch.call_limiter,
    },
  };
};
